// src/utils/freemium.js

// Freemium Configuration
export const FREEMIUM_CONFIG = {
  enabled: process.env.REACT_APP_FREEMIUM_ENABLED === 'true' || process.env.NODE_ENV === 'development',
  swapLimit: parseInt(process.env.REACT_APP_FREEMIUM_SWAP_LIMIT) || 3,
  maskSummaries: process.env.REACT_APP_FREEMIUM_MASK_SUMMARIES !== 'false',
  modalDebounceMs: 60000, // Maximum once per minute
};

// Analytics Event Constants (prevent typos)
export const ANALYTICS_EVENTS = {
  VIEWER_OPEN: 'viewer_open',
  PAYWALL_SHOWN: 'paywall_shown',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  UPGRADE_CLICK: 'upgrade_click',
  MASKED_DAY_CLICK: 'masked_day_click',
  SWAP_CLICK: 'swap_click',
  SWAP_SUCCESS: 'swap_success',
  REGEN_CLICK: 'regen_click',
  REGEN_SUCCESS: 'regen_success',
  EXPORT_CLICK: 'export_click',
};

// Paywall Trigger Types
export const PAYWALL_TRIGGERS = {
  SWAP_LIMIT: 'swap_limit',
  DAY_REGEN: 'day_regen', 
  EXPORT: 'export',
};

// LocalStorage Keys
export const STORAGE_KEYS = {
  SWAP_COUNT: 'alakai_swap_count',
  LAST_PAYWALL_SHOWN: 'alakai_last_paywall',
  PREMIUM_STATUS: 'alakai_premium_status',
};

/**
 * Check if user has premium access
 * For now, check query param or localStorage for testing
 */
export function isPremiumUser() {
  // Check URL param for testing: ?premium=true
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('premium') === 'true') {
    return true;
  }
  
  // Check localStorage for persistent testing
  if (localStorage.getItem(STORAGE_KEYS.PREMIUM_STATUS) === 'true') {
    return true;
  }
  
  // TODO: Replace with real auth check
  return false;
}

/**
 * Analytics helper to track events consistently
 */
export function trackEvent(eventName, properties = {}) {
  if (!FREEMIUM_CONFIG.enabled) return;
  
  const event = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...properties
  };
  
  console.log('[Analytics]', event);
  
  // TODO: Wire up to actual analytics provider (GA, Mixpanel, etc.)
  // For now, just log to console for testing
  
  // Example integration:
  // if (window.gtag) {
  //   window.gtag('event', eventName, properties);
  // }
}

/**
 * Get current swap count from localStorage
 */
export function getSwapCount() {
  const count = parseInt(localStorage.getItem(STORAGE_KEYS.SWAP_COUNT)) || 0;
  return count;
}

/**
 * Increment swap count and return new value
 */
export function incrementSwapCount() {
  const newCount = getSwapCount() + 1;
  localStorage.setItem(STORAGE_KEYS.SWAP_COUNT, newCount.toString());
  return newCount;
}

/**
 * Reset swap count (for testing or on upgrade)
 */
export function resetSwapCount() {
  localStorage.removeItem(STORAGE_KEYS.SWAP_COUNT);
}

/**
 * Check if we should show paywall (debounced)
 */
export function shouldShowPaywall() {
  if (!FREEMIUM_CONFIG.enabled || isPremiumUser()) {
    return false;
  }
  
  const lastShown = parseInt(localStorage.getItem(STORAGE_KEYS.LAST_PAYWALL_SHOWN)) || 0;
  const now = Date.now();
  
  return (now - lastShown) > FREEMIUM_CONFIG.modalDebounceMs;
}

/**
 * Mark paywall as shown (for debouncing)
 */
export function markPaywallShown() {
  localStorage.setItem(STORAGE_KEYS.LAST_PAYWALL_SHOWN, Date.now().toString());
}

/**
 * Check if swap limit is reached
 */
export function isSwapLimitReached() {
  return getSwapCount() >= FREEMIUM_CONFIG.swapLimit;
}

/**
 * Generate teaser chips for masked days
 */
export function generateTeaserChips(dayNumber) {
  const teasers = [
    ['Beach morning', 'Local lunch', 'Sunset viewpoint'],
    ['Adventure hike', 'Farm-to-table', 'Cultural site'], 
    ['Water sports', 'Scenic drive', 'Fine dining'],
    ['Garden tour', 'Coffee tasting', 'Art gallery'],
    ['Snorkel trip', 'Coastal walk', 'Luau feast'],
    ['Helicopter tour', 'Spa time', 'Night market'],
    ['Waterfall hike', 'Fishing charter', 'Rooftop dining']
  ];
  
  // Use dayNumber to get consistent teasers for each day
  const chipSet = teasers[(dayNumber - 2) % teasers.length] || teasers[0];
  return chipSet;
}

const freemiumExports = {
  FREEMIUM_CONFIG,
  ANALYTICS_EVENTS,
  PAYWALL_TRIGGERS,
  STORAGE_KEYS,
  isPremiumUser,
  trackEvent,
  getSwapCount,
  incrementSwapCount,
  resetSwapCount,
  shouldShowPaywall,
  markPaywallShown,
  isSwapLimitReached,
  generateTeaserChips,
};

export default freemiumExports;